import { IDateTimeType } from '@connect-soft/date-helper';
import { FieldErrors, UseFormGetValues, FieldValues } from 'react-hook-form';
import { IDatePickerProps } from '@/fields/DatePicker';
import { IDateRangePickerProps } from '@/fields/DateRangePicker';
import { ITimeRangeOrTimeAndLengthValueType } from '@/fields/TimeRangeOrTimeAndLength';
import { ITimeRangePickerProps } from '@/fields/TimeRangePicker';
import { IItem, ISupportedFieldType } from './FieldTypes';

export type ISupportedValueSimpleType =
    | string
    | number
    | object
    | boolean
    | string[]
    | number[]
    | boolean[]
    | IDateTimeType
    | ITimeRangePickerProps['value']
    | IDatePickerProps['value']
    | IDateRangePickerProps['value']
    | ITimeRangeOrTimeAndLengthValueType;
export type IValuesOfSimpleFields = {
    [name: string]: ISupportedValueSimpleType;
};

export type IValueType = ISupportedValueSimpleType | IValuesOfSimpleFields[];

export type ISupportedValueType = {
    [name: string]: IValueType;
};

export const getFullNameOfField = (fieldName: string, containerName?: string, rowIndex?: number): string =>
    !!containerName && typeof rowIndex === 'number'
        ? `${containerName}.${rowIndex}${!fieldName ? '' : `.${fieldName}`}`
        : containerName
          ? `${containerName}.${fieldName}`
          : fieldName;

export const getSxValue = (width?: number) => ({
    p: 1,
    pb: 3,
    width: typeof width === 'undefined' ? '100%' : `${((width || 12) * 100) / 12}%`
});

export const isFullWidth = <T extends IItem<{ [name: string]: unknown }>>(fullWidthForm: boolean, input: T) =>
    fullWidthForm &&
    ('fullWidth' in input.props ? input.props.fullWidth === true : true) &&
    typeof input.props.width === 'undefined';

const valueGetter = (item: ISupportedFieldType): ISupportedValueSimpleType => {
    switch (item.type) {
        case 'multiChipSelect':
        case 'multiSelect':
        case 'transferList':
            return (item.props.value ?? []) as string[];
        case 'checkBox':
        case 'switch':
            return (item.props.value || false) as boolean;
        case 'dateRangePicker':
            return item.props.value ?? { start: null, end: null };
        case 'timeRangePicker':
            return item.props.value ?? { start: null, duration: null };
        case 'timeRangeOrTimeAndLength':
            return item.props.value ?? { start: null, duration: null, isRange: false };
        case 'weekdays':
            return (item.props.value ?? 0) as number;
        case 'html':
            return '';
        case 'select':
        case 'colorPicker':
        case 'password':
        case 'textArea':
        case 'textField':
            return (item.props.value || '') as string;
        case 'datePicker':
        case 'timePicker':
            return item.props.value ?? null;
        default:
            throw Error(`Not implemented support defaultValue for item of type [${item.type}]`);
    }
};

export const getValuesFromProps = (fields: ISupportedFieldType[]): ISupportedValueType => {
    const data: ISupportedValueType = {};

    fields.forEach((item) => {
        switch (item.type) {
            case 'custom': {
                const values = item.props.values;

                Object.keys(values).forEach((fieldName) => (data[fieldName] = values[fieldName]));
                break;
            }
            case 'multiRowInputs': {
                data[item.props.name] =
                    item.props.value && (item.props.value.length > 0 || item.props.fixedSize)
                        ? item.props.value
                        : ([getValuesFromProps(item.props.inputs)] as IValuesOfSimpleFields[]);
                break;
            }
            case 'tabs': {
                data[item.props.name] = item.props.value;

                item.props.tabs.forEach((tab) => {
                    const innerData = getValuesFromProps(tab.inputs);

                    Object.keys(innerData).forEach((fieldName) => (data[fieldName] = innerData[fieldName]));
                });
                break;
            }
            case 'block': {
                const innerData = getValuesFromProps(item.props.fields);

                if (item.props.wrapNameOfFields !== false) {
                    data[item.props.name] = innerData;
                } else {
                    Object.keys(innerData).forEach((fieldName) => (data[fieldName] = innerData[fieldName]));
                }

                break;
            }
            case 'button':
            case 'newLine':
                break;
            default:
                if (!item.changeValueOnlyFromInside) {
                    data[item.props.name] = valueGetter(item);
                }

                break;
        }
    });

    return data;
};

type IFieldErrorType = { type: string; message: string };

export const getErrorByFieldName = (errors: FieldErrors, fieldName: string): IFieldErrorType | undefined => {
    const parts = fieldName.split('.');

    if (parts.length > 1) {
        const subErrors = errors[parts[0]];

        if (subErrors) {
            return getErrorByFieldName(subErrors as FieldErrors, parts.slice(1).join('.'));
        }

        return undefined;
    } else {
        return errors[parts[0]] as IFieldErrorType;
    }
};

const isEmptyValue = (value: unknown[] | unknown) =>
    Array.isArray(value) ? value.length === 0 : !Boolean(value).valueOf();
const valuesGetter = (getValues: FieldValues | UseFormGetValues<FieldValues>) =>
    typeof getValues === 'function' ? getValues : () => getValues;
const isDisplayed = (
    getValues: FieldValues | UseFormGetValues<FieldValues>,
    shouldDisplay: boolean | ((inputs: FieldValues) => boolean),
    hidden: boolean
): boolean => {
    const values = typeof shouldDisplay === 'function' && !hidden ? valuesGetter(getValues)() : {};

    return !hidden && (typeof shouldDisplay === 'function' ? shouldDisplay(values) : shouldDisplay);
};
const isRequired = (
    getValues: FieldValues | UseFormGetValues<FieldValues>,
    required: boolean | ((inputs: FieldValues) => boolean)
) => {
    const values = typeof required === 'function' ? valuesGetter(getValues)() : {};

    return typeof required === 'function' ? required(values) : required;
};

export const requiredAndDisplay = (
    getValues: UseFormGetValues<FieldValues>,
    required: boolean | ((inputs: FieldValues) => boolean) = false,
    shouldDisplay: boolean | ((inputs: FieldValues) => boolean) = true,
    hidden = false
) => {
    const values =
        typeof required === 'function' || (typeof shouldDisplay === 'function' && !hidden) ? getValues() : {};

    return {
        values,
        hidden: !isDisplayed(values, shouldDisplay, hidden),
        required: isRequired(values, required)
    };
};
export const callableValidationOfRequired = (
    currentValue: unknown,
    getValues: UseFormGetValues<FieldValues>,
    requiredErrorMessage: string,
    required?: boolean | ((inputs: FieldValues) => boolean),
    shouldDisplay?: boolean | ((inputs: FieldValues) => boolean),
    hidden = false
) => {
    const data = requiredAndDisplay(getValues, required ?? false, shouldDisplay ?? true, hidden ?? false);

    // eslint-disable-next-line no-console
    console.log({ data });

    return data.required && data.hidden && isEmptyValue(currentValue) ? requiredErrorMessage : undefined;
};

export const getContentByFiledName = (
    fieldName: string,
    content: { [name: string]: IValueType | IValuesOfSimpleFields }
): undefined | IValueType | IValuesOfSimpleFields => {
    const parts = fieldName.split('.');

    const subContent = content[parts[0]];

    if (parts.length > 1) {
        if (subContent) {
            return getContentByFiledName(
                parts.slice(1).join('.'),
                subContent as { [name: string]: IValueType | IValuesOfSimpleFields }
            );
        }

        return undefined;
    } else {
        return subContent;
    }
};
